import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../config/firebase';
import { Auction, AuctionLot } from '../types/auction.types';
import { useAuth } from '../contexts/AuthContext';
import { BidPanel } from '../components/bidding/BidPanel';
import { CountdownTimer } from '../components/auction/CountdownTimer';
import WatchButton from '../components/bidding/WatchButton';
import { useWatchlist } from '../hooks/useWatchlist';
import { contentImages } from '../utils/lotImages';
import { deriveSaleState, closedLotStatus, registrationClosedText } from '../utils/saleState';
import IWantItButton from '../components/iwi/IWantItButton';
import { format } from 'date-fns';
import toast from 'react-hot-toast';

// Consignor identity is CONFIDENTIAL — a consignor's supply chain must not be public. Only
// staff roles may see it on the (public) lot detail page.
const STAFF_ROLES = ['seller', 'admin', 'consignor', 'auctioneer'];

const LotDetail: React.FC = () => {
  const { auctionId, lotId } = useParams<{ auctionId: string; lotId: string }>();
  const { userProfile } = useAuth();
  const isStaff = !!userProfile && STAFF_ROLES.includes(userProfile.role);
  const navigate = useNavigate();
  const { isWatched, toggle: toggleWatch } = useWatchlist();
  
  const [auction, setAuction] = useState<Auction | null>(null);
  const [lot, setLot] = useState<AuctionLot | null>(null);
  const [loading, setLoading] = useState(true);
  
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [showImageGallery, setShowImageGallery] = useState(false);
  const [jumpTo, setJumpTo] = useState('');
  // lotNumber -> lotId for "jump to lot", refreshed on every snapshot WITHOUT a re-render.
  const lotIndexRef = useRef<{ id: string; lotNumber: unknown }[]>([]);
  // Fingerprint of what THIS page shows; a snapshot that leaves it unchanged is dropped (see below).
  const shownKeyRef = useRef<string>('');

  // Jump to another lot number within the SAME auction. Resolves lotNumber -> lotId from the
  // auction doc already loaded on this page (no extra read). Errors inline if not found.
  const handleJump = (e?: React.FormEvent) => {
    e?.preventDefault();
    const num = jumpTo.trim();
    if (!num) return;
    const target = lotIndexRef.current.find((l) => String(l.lotNumber) === num);
    if (!target) {
      toast.error(`No lot #${num} in this sale`);
      return;
    }
    setJumpTo('');
    if (target.id === lotId) return; // already viewing it
    navigate(`/auctions/${auctionId}/lots/${target.id}`);
  };

  // LIVE sale document (Oct 2026). This was a one-time getDoc, so a soft-close extension never
  // reached the lot page: its countdown ran to 0:00 while the lot stayed open for another three
  // minutes, and the bid panel's 'live' status came from that same stale read.
  //
  // The lots live in ONE array on the sale document, so one listener keeps this lot current. That
  // document is also the bid cache — it is rewritten on EVERY bid anywhere in the sale — so the
  // listener selects THIS lot and drops any snapshot that changes nothing this page shows: a bid on
  // lot 7 does not re-render a bidder sitting on lot 90. What this page shows = the sale's own
  // fields (minus updatedAt, which every bid rewrites), this lot, and the latest effective end of
  // any lot (all deriveSaleState needs from the others, for "is the sale over").
  useEffect(() => {
    if (!auctionId || !lotId) return;
    setLoading(true);
    shownKeyRef.current = '';
    const ms = (v: any): number | null => (v?.toMillis ? v.toMillis() : v instanceof Date ? v.getTime() : null);
    const keyOf = (v: any): any => {
      if (v == null || typeof v !== 'object') return v;
      if (typeof v.toMillis === 'function') return { _ts: v.toMillis() };
      if (Array.isArray(v)) return v.map(keyOf);
      return Object.fromEntries(Object.keys(v).sort().map((k) => [k, keyOf(v[k])]));
    };
    const unsub = onSnapshot(
      doc(db, 'auctions', auctionId),
      (snap) => {
        if (!snap.exists()) {
          toast.error('Auction not found');
          navigate('/auctions');
          return;
        }
        const data: any = snap.data();
        const rawLots: any[] = data.lots || [];
        lotIndexRef.current = rawLots.map((l) => ({ id: l.id, lotNumber: l.lotNumber }));
        const rawLot = rawLots.find((l) => l.id === lotId);
        if (!rawLot) {
          toast.error('Lot not found');
          navigate(`/auctions/${auctionId}`);
          return;
        }
        const { lots: _lots, updatedAt: _u, ...meta } = data;
        const latestEndMs = rawLots.reduce((m, l) => Math.max(m, ms(l.actualEndTime) ?? ms(l.scheduledEndTime) ?? 0), 0);
        const key = JSON.stringify(keyOf({ meta, rawLot, latestEndMs }));
        if (key === shownKeyRef.current) return; // a change elsewhere in the sale — nothing to redraw
        shownKeyRef.current = key;

        const convLot = (l: any) => ({
          ...l,
          createdAt: l.createdAt?.toDate?.() ?? l.createdAt,
          scheduledEndTime: l.scheduledEndTime?.toDate?.() ?? l.scheduledEndTime,
          actualEndTime: l.actualEndTime?.toDate?.() ?? l.actualEndTime,
          lastExtensionAt: l.lastExtensionAt?.toDate?.() ?? l.lastExtensionAt,
        });
        // deriveSaleState needs only "is any lot still open": a lot carrying the sale's latest end
        // stands in for the rest, so the others need not be converted or kept current here.
        const latestLot = rawLots.find((l) => (ms(l.actualEndTime) ?? ms(l.scheduledEndTime) ?? 0) === latestEndMs);
        const lotsForState = latestLot && latestLot.id !== rawLot.id ? [convLot(rawLot), convLot(latestLot)] : [convLot(rawLot)];
        const loadedAuction: Auction = {
          id: snap.id,
          ...meta,
          startDate: data.startDate?.toDate?.() ?? data.startDate,
          endDate: data.endDate?.toDate?.() ?? data.endDate,
          createdAt: data.createdAt?.toDate?.() ?? data.createdAt,
          updatedAt: data.updatedAt?.toDate?.() ?? data.updatedAt,
          lots: lotsForState,
        } as Auction;
        setAuction(loadedAuction);
        setLot(lotsForState[0] as AuctionLot);
        setLoading(false);
      },
      (error) => {
        console.error('Error loading lot:', error);
        toast.error('Failed to load lot details');
        setLoading(false);
      },
    );
    return () => unsub(); // clean up on unmount and on every lot/sale change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auctionId, lotId]);

  /**
   * The photos a bidder sees, barcode frame removed.
   *
   * This used to be `images.length > 1 ? images.slice(1) : []` — drop whatever
   * is in slot 0 and hope it was the barcode. That is the positional assumption
   * lotImages.ts was written to kill, and it is wrong in two ways that both
   * reach the bidder:
   *
   *   • if the _00 frame ISN'T first (or the lot has none), slice(1) throws away
   *     a real item photo AND promotes the barcode to the main image — a lot
   *     detail page showing a barcode where the item should be;
   *   • a lot with exactly one photo (_01 only, no barcode) fails `length > 1`
   *     and renders "No Images Available" over a photo that exists.
   *
   * contentImages() decides by FILENAME, so neither depends on array order.
   */
  const getDisplayImages = (images: string[]) => contentImages(images);

  const openImageGallery = (index: number) => {
    setSelectedImageIndex(index);
    setShowImageGallery(true);
  };

  const closeImageGallery = () => {
    setShowImageGallery(false);
  };

  const nextImage = () => {
    if (!lot) return;
    const displayImages = getDisplayImages(lot.images);
    setSelectedImageIndex((prev) => 
      prev < displayImages.length - 1 ? prev + 1 : 0
    );
  };

  const prevImage = () => {
    if (!lot) return;
    const displayImages = getDisplayImages(lot.images);
    setSelectedImageIndex((prev) => 
      prev > 0 ? prev - 1 : displayImages.length - 1
    );
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Loading lot details...</p>
      </div>
    );
  }

  if (!auction || !lot) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Lot not found</p>
      </div>
    );
  }

  const displayImages = getDisplayImages(lot.images);

  return (
    <div className="space-y-6">
      {/* Breadcrumb + jump-to-lot */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-sm">
          <Link to="/auctions" className="text-primary-600 hover:text-primary-700">
            Auctions
          </Link>
          <span className="text-gray-400">›</span>
          <Link to={`/auctions/${auctionId}`} className="text-primary-600 hover:text-primary-700">
            {auction.title}
          </Link>
          <span className="text-gray-400">›</span>
          <span className="text-gray-600">LOT {lot.lotNumber}</span>
          <span className="text-gray-400">·</span>
          <Link to={`/sale/${auctionId}/info`} className="text-primary-600 hover:text-primary-700">
            Sale Info &amp; Terms
          </Link>
        </div>
        <form onSubmit={handleJump} className="flex items-center gap-2">
          <label htmlFor="jump-lot" className="text-sm text-gray-600 whitespace-nowrap">
            Jump to lot #
          </label>
          <input
            id="jump-lot"
            type="number"
            inputMode="numeric"
            min="1"
            value={jumpTo}
            onChange={(e) => setJumpTo(e.target.value)}
            placeholder="e.g. 12"
            className="input py-1 w-24"
          />
          <button type="submit" className="btn-secondary py-1 px-3 text-sm">
            Go
          </button>
        </form>
      </div>

      {/* Main Content */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Left Column: Images */}
        <div className="lg:col-span-2 space-y-4">
          {/* Main Image */}
          <div className="card">
            {displayImages.length > 0 ? (
              <div>
                <button
                  onClick={() => openImageGallery(0)}
                  className="w-full group cursor-pointer"
                >
                  <img
                    src={displayImages[0]}
                    alt={lot.title}
                    className="w-full h-96 object-contain bg-gray-50 rounded-lg group-hover:opacity-90 transition"
                  />
                </button>
                
                {/* Thumbnail Strip */}
                {displayImages.length > 1 && (
                  <div className="mt-4 grid grid-cols-6 gap-2">
                    {displayImages.slice(0, 6).map((img, i) => (
                      <button
                        key={i}
                        onClick={() => openImageGallery(i)}
                        className="relative group"
                      >
                        <img
                          src={img}
                          alt={`${lot.title} - ${i + 1}`}
                          className="w-full h-20 object-cover rounded border-2 border-gray-200 group-hover:border-primary-500 transition"
                        />
                        {i === 5 && displayImages.length > 6 && (
                          <div className="absolute inset-0 bg-black bg-opacity-50 rounded flex items-center justify-center text-white font-bold">
                            +{displayImages.length - 6}
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="w-full h-96 bg-gray-100 rounded-lg flex items-center justify-center">
                <span className="text-gray-400">No Images Available</span>
              </div>
            )}
          </div>

          {/* Lot Details */}
          <div className="card">
            <div className="flex items-start justify-between gap-3 mb-2">
              <h1 className="text-3xl font-bold">{lot.title}</h1>
              <WatchButton
                variant="full"
                watched={isWatched(auctionId!, lotId!)}
                onToggle={() => toggleWatch({ auctionId: auctionId!, lotId: lotId!, lotNumber: lot.lotNumber, lotTitle: lot.title, addedAt: Date.now() })}
                className="shrink-0 mt-1"
              />
            </div>
            
            <div className="flex items-center gap-3 mb-4">
              <span className="text-sm font-semibold text-gray-600">
                LOT {lot.lotNumber}
              </span>
              <span className="text-sm text-gray-400">•</span>
              <span className="text-sm text-gray-600">SKU: {lot.sku}</span>
              
              {(lot.hibidCategory || lot.category) && (
                <>
                  <span className="text-sm text-gray-400">•</span>
                  <span className="text-xs bg-gray-100 px-2 py-1 rounded">
                    📁 {lot.hibidCategory || lot.category}
                  </span>
                </>
              )}
            </div>

            <div className="prose max-w-none">
              <p className="text-gray-700 whitespace-pre-wrap">{lot.description}</p>
            </div>

            {/* I WANT IT — permanent on a sold lot, once its sale is over.
                Phase 0 records interest and sends nothing; the component says so. */}
            {(() => {
              const state = deriveSaleState(auction);
              const saleOver = state === 'ended' || state === 'confirmed';
              const sold = lot.status === 'sold' || (lot.bidCount || 0) > 0;
              if (!saleOver || !sold) return null;
              const endedAt = auction.endDate
                ? ((auction.endDate as any).toDate ? (auction.endDate as any).toDate() : new Date(auction.endDate as any))
                : null;
              return (
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <IWantItButton
                    source="past-auction-lot"
                    item={{
                      // No TTR code on a whitelabelbuddy lot today (AuctionLot has `sku`).
                      // Stored as null so the field exists the day lots carry one.
                      ttrCode: null,
                      auctionId: auctionId || null,
                      lotId: lotId || null,
                      lotNumber: lot.lotNumber || null,
                      sku: lot.sku || null,
                      itemTitle: lot.title || null,
                    }}
                    lastKnownSaleId={auctionId || null}
                    lastKnownSaleTitle={auction.title || null}
                    lastKnownSaleEndedAt={endedAt}
                  />
                </div>
              );
            })()}

            {/* Additional Details */}
            <div className="mt-6 grid md:grid-cols-2 gap-4">
              {lot.measures && (
                <div>
                  <p className="text-sm font-semibold text-gray-600">Measurements</p>
                  <p className="text-gray-800">{lot.measures}</p>
                </div>
              )}
              
              {lot.condition && (
                <div>
                  <p className="text-sm font-semibold text-gray-600">Condition</p>
                  <p className="text-gray-800">{lot.condition}</p>
                </div>
              )}
              
              {/* Consignor is STAFF-ONLY — never rendered to the public (identity leak). */}
              {isStaff && lot.consignor && (
                <div>
                  <p className="text-sm font-semibold text-gray-600">
                    Consignor <span className="text-xs font-normal text-gray-400">(staff only)</span>
                  </p>
                  <p className="text-gray-800">{lot.consignor}</p>
                </div>
              )}
            </div>
          </div>

          {/* Auction Info */}
          <div className="card bg-gray-50">
            <h2 className="text-xl font-bold mb-4">Auction Information</h2>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-600">Auction:</span>
                <Link 
                  to={`/auctions/${auctionId}`}
                  className="text-primary-600 hover:text-primary-700 font-semibold"
                >
                  {auction.title}
                </Link>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Start Date:</span>
                <span className="font-semibold">
                  {format(auction.startDate, 'MMM dd, yyyy h:mm a')}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">End Date:</span>
                <span className="font-semibold">
                  {format(auction.endDate, 'MMM dd, yyyy h:mm a')}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Location:</span>
                <span className="font-semibold">{auction.location || 'N/A'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Status:</span>
                <span className={`font-semibold ${
                  auction.status === 'live' ? 'text-green-600' :
                  auction.status === 'scheduled' ? 'text-blue-600' :
                  'text-gray-600'
                }`}>
                  {auction.status.toUpperCase()}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column: Bidding */}
        <div className="lg:col-span-1">
          <div className="sticky top-4 space-y-4">
            {/* Countdown Timer Card */}
            {auction.status === 'live' && lot.actualEndTime && (
              <div className="card text-center">
                <p className="text-sm text-gray-600 mb-2">Time Remaining</p>
                <CountdownTimer
                  endTime={lot.actualEndTime}
                  scheduledEndTime={lot.scheduledEndTime}
                  hardStop={auction.biddingHardStop}
                  softCloseWindowSeconds={auction.softCloseWindow}
                  size="large"
                  closedStatus={closedLotStatus(auction, lot)}
                />
                <p className="text-xs text-gray-500 mt-3">
                  Closes: {format(lot.actualEndTime, 'h:mm:ss a')}
                </p>
                {lot.scheduledEndTime && lot.actualEndTime > lot.scheduledEndTime && (
                  <p className="text-xs text-gray-500">
                    Originally: {format(lot.scheduledEndTime, 'h:mm:ss a')}
                  </p>
                )}
              </div>
            )}
            
            <BidPanel
              auctionId={auctionId!}
              lotId={lotId!}
              lotNumber={lot.lotNumber}
              lotTitle={lot.title}
              startingBid={lot.startingBid}
              auctionStatus={auction.status}
              bidIncrements={auction.bidIncrements}
              registrationClosedText={registrationClosedText(auction)}
            />
          </div>
        </div>
      </div>

      {/* Image Gallery Modal */}
      {showImageGallery && displayImages.length > 0 && (
        <div
          className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4"
          onClick={closeImageGallery}
        >
          <button
            onClick={closeImageGallery}
            className="absolute top-4 right-4 text-white text-4xl hover:text-gray-300 z-50"
          >
            ×
          </button>

          {displayImages.length > 1 && (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  prevImage();
                }}
                className="absolute left-4 text-white text-6xl hover:text-gray-300 z-50"
              >
                ‹
              </button>

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  nextImage();
                }}
                className="absolute right-4 text-white text-6xl hover:text-gray-300 z-50"
              >
                ›
              </button>
            </>
          )}

          <div
            className="max-w-5xl max-h-[90vh] relative"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={displayImages[selectedImageIndex]}
              alt={`${lot.title} - ${selectedImageIndex + 1}`}
              className="max-w-full max-h-[90vh] object-contain rounded"
            />
            {displayImages.length > 1 && (
              <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-75 text-white px-4 py-2 rounded">
                {selectedImageIndex + 1} / {displayImages.length}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default LotDetail;